// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * xng-changes:
 * Rug-30:
 *  1. change FormField import path
 *  2. pass currentValue to FormField component
 */

import { TextAreaComponentProps, TextAreaPresentationProps } from "@insite/mobius/TextArea";
import { HasDisablerContext, withDisabler } from "@insite/mobius/utilities/DisablerContext";
import omitMultiple from "@insite/mobius/utilities/omitMultiple";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import { FormFieldPresentationProps } from "@rugby/MobiusOverrides/FormField";
import FormField from "@rugby/MobiusOverrides/FormField";
import * as React from "react";

export type TextAreaProps = FormFieldPresentationProps<TextAreaComponentProps> &
    TextAreaComponentProps & TextAreaPresentationProps & { hasValue?: boolean };

/**
 * TextArea is a form element with an optional label, hint text, error message and optional icon.
 * Props not contained in the list below get passed into the input component (e.g. event handlers, `value`, etc).
 */
const TextArea: React.FC<TextAreaProps & HasDisablerContext> = props => {
    const { disable, disabled, error, hint, id, placeholder, required, hasValue, ...otherProps } = props;
    // Because disabled html attribute doesn't accept undefined
    // eslint-disable-next-line no-unneeded-ternary
    const isDisabled = disable || disabled ? true : false;
    const inputId = id || uniqueId();
    const labelId = `${inputId}-label`;
    const inputLabelObj = otherProps.label === 0 || otherProps.label ? { "aria-labelledby": labelId } : {};
    const descriptionId = `${inputId}-description`;
    const hasDescription = error || hint;

    const textInput = (
        <>
            <textarea
                id={inputId}
                aria-describedby={hasDescription ? descriptionId : undefined}
                aria-invalid={!!error}
                aria-required={!disabled && required}
                tabIndex={0}
                {...{ disabled: isDisabled, placeholder, required }}
                {...omitMultiple(otherProps, ["sizeVariant", "border", "label", "backgroundColor", "cssOverrides"])}
                {...inputLabelObj}
            />
        </>
    );

    // RUG-30: pass currentValue
    return (
        <FormField
            descriptionId={descriptionId}
            formInput={textInput}
            labelId={labelId}
            inputId={inputId}
            disabled={isDisabled}
            currentValue={otherProps.value || (hasValue && "has-value")}
            {...props}
        />
    );
};

TextArea.defaultProps = {
    rows: 4,
};

TextArea.displayName = "TextArea";
/** @component */
export default withDisabler(TextArea);
